/**
 * WebinarVazlatScreen — Webinár Stratégiai Vázlat jóváhagyó képernyő.
 *
 * Bal panel = F3 / Facebook posztvázlat finomító panel IDENTIKUS struktúrája:
 *   - kijelölés-kártya („KIJELÖLT RÉSZ" + X)
 *   - 6 gyorsgomb (Élesebb, Lágyabb, Konkrétabb, Több sztori, Humorosabb,
 *     Lepj meg!)
 *   - SuggestionCard (Alkalmazom / Új verziót / Mégsem)
 *   - AppliedBlock (Frissítve.)
 *   - alsó chat-rúd (Paperclip + input + gradient send + tip sor)
 *
 * Jobb oldal = krém dokumentum, prémium tipográfia, arany akcentus, fehér
 * pill szekciók, 3 győzelem mini-kártya, szekcióterv roadmap-szerűen.
 *
 * CTA = rendszerszintű lila gomb, kevésbé lekerekített, finom glow,
 * shimmer animáció generálás közben.
 */
import React, { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy as CopyIcon,
  DownloadSimple,
  CheckCircle,
  CircleNotch,
  PaperPlaneTilt,
  Paperclip,
  X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getActiveAlap } from "../lib/activeAlap";
import { PostHeader } from "./MagnetPostFacebook";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";
import MagicGenerateButton from "../components/MagicGenerateButton";

// 6 gyorsgomb — IDENTIKUS a MagnetPostFacebook QUICK_REFINE_ACTIONS-ával.
const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb",    instruction: "Erősebb érzelmi feszültség, mélyebb fájdalom, konkrétabb jelenet, nagyobb hatás." },
  { id: "soft",     label: "Lágyabb",    instruction: "Melegebb, empatikusabb, barátságosabb, több megértés." },
  { id: "concrete", label: "Konkrétabb", instruction: "Több konkrét részlet, időpont, hely, tárgy, érzés — semmi általánosítás." },
  { id: "story",    label: "Több sztori", instruction: "Cseréld ki vagy bővítsd ki egy rövid, életszagú személyes vagy hipotetikus jelenettel." },
  { id: "humor",    label: "Humorosabb", instruction: "Adj bele finom öniróniát vagy könnyed humort, de maradj empatikus." },
  { id: "surprise", label: "Lepj meg!",  instruction: "Legyél kreatívabb, váratlanabb, erősebb hook vagy fordulat ebben a részben." },
];

const GOLD = "#ffdb5c";
const GOLD_DEEP = "#d4a800";

// Cream-action chip — IDENTIKUS a FB poszt eredményképernyő pill-jével
// (apró eltérés: az `accent` color paraméterezhető, így az Értékesítés
// arany akcentus passzol a Webinár layerhez).
function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled, accent = "#75dff5" }) {
  const accentBg = active ? "rgba(29,158,117,0.10)" : `${accent}14`;
  const accentBorder = active ? "rgba(29,158,117,0.30)" : `${accent}3d`;
  const accentText = active ? "#1d9e75" : "#7a5a00";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: accentBg,
        border: `1px solid ${accentBorder}`,
        borderRadius: 999,
        color: accentText,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}

// Pill szekciófej a krém doksin — arany layer-akcent chip.
function SectionPill({ children, testid }) {
  const raw = String(children);
  const [head, ...rest] = raw.split("·");
  return (
    <div
      data-testid={testid}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        fontSize: 12,
        letterSpacing: "0.20em",
        textTransform: "uppercase",
        fontWeight: 700,
        background: "linear-gradient(135deg, #c29512 0%, #a87a08 100%)",
        border: "1px solid #d4a800",
        borderRadius: 999,
        padding: "9px 18px",
        marginBottom: 22,
        boxShadow: "0 1px 0 rgba(255,255,255,0.22) inset, 0 4px 10px -4px rgba(168,122,8,0.45)",
      }}
    >
      <span style={{ color: "#fffae6", fontWeight: 800 }}>{head.trim()}</span>
      <span style={{ color: "#fffae6", opacity: 0.55 }}>·</span>
      <span style={{ color: "#ffffff" }}>{rest.join("·").trim()}</span>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div className="font-heading" style={{
      fontSize: 19,
      fontWeight: 700,
      color: "#0a0a12",
      letterSpacing: "-0.012em",
      lineHeight: 1.3,
      marginBottom: 10,
    }}>{children}</div>
  );
}

function PlainBlock({ text }) {
  if (!text) return null;
  return (
    <div className="webinar-vazlat-selectable" style={{
      whiteSpace: "pre-wrap", fontSize: 15.5, color: "#0a0a12",
      lineHeight: 1.7,
    }}>{text}</div>
  );
}

function SectionShell({ children, testid }) {
  return (
    <section
      data-testid={testid}
      style={{ padding: "30px 0 6px", borderBottom: "1px solid rgba(10,10,18,0.08)" }}
    >
      {children}
    </section>
  );
}

export default function WebinarVazlatScreen({
  vazlat, vazlatRaw, jobId, inputs,
  onVazlatRawChange = () => {},
  onStartWriter = () => {},
  onBack = () => {},
  writerLoading = false,
  writerLoadingLabel = "",
  agentVariant = "callbook_funnel",
}) {
  // Variant delta: a 04-es blokk labelje a Közvetlen értékesítés
  // (direct_sales) agentnél NEM „Telefonhívás íve", hanem
  // „Ajánlati döntés íve". A mezők (hivas_neve / hivas_fo_igeret /
  // hivas_4_dolog) tartalmilag ilyenkor az ajánlati / vásárlási
  // döntés íve — a backend vázlat prompt override-ja gondoskodik
  // arról, hogy ott program név / promise / kulcs döntési pontok
  // jöjjön, nem hívás-fókuszú tartalom.
  const isDirect = agentVariant === "direct_sales";
  const blk04Title = isDirect ? "04 · Ajánlati döntés íve" : "04 · Telefonhívás íve";
  const blk04Field1Label = isDirect ? "Ajánlat / program neve" : "Hívás neve";
  const blk04Field2Label = isDirect ? "Az ajánlat fő ígérete" : "Hívás fő ígérete";
  const blk04Field3Label = isDirect
    ? "Kulcs döntési pontok"
    : "A híváson 4 dolgot nézünk meg";
  // ── Refine state ──────────────────────────────────────────────────
  const [chatInput, setChatInput] = useState("");
  const [selection, setSelection] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle"); // idle|loading|suggestion|applied|error
  const [suggestion, setSuggestion] = useState(null);     // {original, snippet, instr}
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [applyingWrite, setApplyingWrite] = useState(false);
  const [showApplied, setShowApplied] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const [writerStarting, setWriterStarting] = useState(false);
  const [lastInstr, setLastInstr] = useState("");
  const [copied, setCopied] = useState(false);
  const [savingToVault, setSavingToVault] = useState(false);
  const [savedToVault, setSavedToVault] = useState(false);
  const containerRef = useRef(null);

  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.userAgent || "");
  const isLoading = refinePhase === "loading";
  const showSuggestion = refinePhase === "suggestion" && suggestion !== null;
  const inputDisabled = isLoading || showSuggestion;

  // Watch text selection within the cream doc area.
  const handleMouseUp = () => {
    try {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const txt = String(sel.toString() || "").trim();
      if (!txt || txt.length < 4) return;
      const node = sel.anchorNode;
      const within = node && containerRef.current &&
        (containerRef.current.contains(node) || containerRef.current === node);
      if (!within) return;
      setSelection(txt);
    } catch { /* noop */ }
  };

  const clearSelection = () => {
    setSelection("");
    try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ }
  };

  const callRefine = async (quote, utasitas) => {
    const activeAlap = getActiveAlap();
    const res = await api.post("/conversion/webinar/vazlat/refine", {
      voice_id: activeAlap.voice || null,
      client_id: activeAlap.client || null,
      offer_id: activeAlap.offer || null,
      ...inputs,
      vazlat_raw: vazlatRaw,
      utasitas,
      quote,
    });
    return res.data?.snippet || "";
  };

  const runRefine = async (utasitas, quote) => {
    const u = (utasitas || "").trim();
    const q = (quote || "").trim();
    if (!u) { toast.message("Add meg, mit változtassak."); return; }
    if (!q) { toast.message("Jelölj ki egy részt a vázlatból."); return; }
    setRefinePhase("loading");
    setSuggestionError(false);
    setLastInstr(u);
    try {
      const snippet = await callRefine(q, u);
      if (!snippet) {
        setSuggestionError(true);
        setSuggestion({ original: q, snippet: "", instr: u });
        setRefinePhase("suggestion");
        return;
      }
      setSuggestion({ original: q, snippet, instr: u });
      setRefinePhase("suggestion");
    } catch (e) {
      const msg = e?.response?.data?.detail || "A finomítás sikertelen volt.";
      setSuggestionError(true);
      setSuggestion({ original: q, snippet: "", instr: u, error: msg });
      setRefinePhase("suggestion");
      toast.error(msg);
    }
  };

  const submitChat = () => {
    runRefine(chatInput, selection);
  };

  const onQuickAction = (qa) => {
    if (!selection) return;
    runRefine(qa.instruction, selection);
  };

  const onRetryVariant = async () => {
    if (!suggestion || !lastInstr) return;
    setRegenerating(true);
    try {
      const snippet = await callRefine(suggestion.original, lastInstr);
      if (snippet) {
        setSuggestion({ ...suggestion, snippet });
        setSuggestionError(false);
      } else {
        setSuggestionError(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Új verzió készítése sikertelen.");
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const onRetryAfterError = () => {
    if (!suggestion) return;
    runRefine(suggestion.instr || lastInstr, suggestion.original);
  };

  const onCancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const onApplySuggestion = async () => {
    if (!suggestion || !suggestion.snippet || applyingWrite) return;
    setApplyingWrite(true);
    try {
      const original = suggestion.original;
      const snippet = suggestion.snippet;
      let newRaw = vazlatRaw;
      const idx = newRaw.indexOf(original);
      if (idx === -1) {
        toast.error("A kijelölt szöveg nem található már a vázlatban.");
        return;
      }
      const prevRaw = newRaw;
      const prevVazlat = vazlat;
      newRaw = newRaw.slice(0, idx) + snippet + newRaw.slice(idx + original.length);
      const res = await api.post("/conversion/webinar/vazlat/apply", {
        job_id: jobId || null,
        vazlat_raw: newRaw,
      });
      const newParsed = res.data?.vazlat || null;
      onVazlatRawChange(newRaw, newParsed);
      setUndoSnapshot({ raw: prevRaw, parsed: prevVazlat });
      setChatInput("");
      setSelection("");
      setSuggestion(null);
      setRefinePhase("idle");
      setShowApplied(true);
      try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ }
      setTimeout(() => setShowApplied(false), 4200);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Az alkalmazás sikertelen volt.";
      toast.error(msg);
    } finally {
      setApplyingWrite(false);
    }
  };

  const onUndoApplied = async () => {
    if (!undoSnapshot) return;
    try {
      await api.post("/conversion/webinar/vazlat/apply", {
        job_id: jobId || null,
        vazlat_raw: undoSnapshot.raw,
      });
      onVazlatRawChange(undoSnapshot.raw, undoSnapshot.parsed);
      setUndoSnapshot(null);
      setShowApplied(false);
      toast.success("Visszavonva.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Visszavonás sikertelen.");
    }
  };

  // ── 3 győzelem render ─────────────────────────────────────────────
  const gyozelmek = useMemo(() => {
    const arr = Array.isArray(vazlat?.gyozelmek) ? vazlat.gyozelmek : [];
    return arr.slice(0, 3);
  }, [vazlat]);

  const handleStartWriter = async () => {
    if (writerStarting || writerLoading) return;
    setWriterStarting(true);
    try {
      // A szülő `startWebinarGeneration` egy async Promise-t ad vissza.
      // Siker esetén a szülő `setPhase("carousel")` átvált → ez a komponens
      // unmount lesz. Hiba esetén viszont a szülő `catch`-je elnyeli a
      // hibát és a felhasználó itt marad — ezért a `finally`-ben MUSZÁJ
      // visszaállítani a `writerStarting` állapotot, különben a lila gomb
      // örökre „Indul a writer…” spinneren ragad. (2026-02 bugfix.)
      await onStartWriter();
    } finally {
      setWriterStarting(false);
    }
  };

  // ── Akciók (Másolás / Letöltés / Mentés) ─────────────────────────
  // Mentés a Tudástárba a vázlat fázisban DISABLED — a meglévő
  // /conversion/webinar/callbook-funnel/save endpoint a TELJES generált
  // webinár tartalmát várja, így mentés csak a writer után lesz aktív.
  const handleCopy = async () => {
    const text = vazlatRaw || "";
    if (!text) { toast.message("Nincs mit másolni."); return; }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Vázlat a vágólapra másolva.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Másolás sikertelen.");
    }
  };

  const handleDownload = () => {
    const text = vazlatRaw || "";
    if (!text) { toast.message("Nincs mit letölteni."); return; }
    const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const cim = (vazlat?.webinar_cim || "webinar-vazlat").toString()
      .toLowerCase()
      .replace(/[áéíóöőúüű]/g, (c) => ({á:"a",é:"e",í:"i",ó:"o",ö:"o",ő:"o",ú:"u",ü:"u",ű:"u"}[c] || c))
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || "webinar-vazlat";
    const a = document.createElement("a");
    a.href = url;
    a.download = `${cim}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSaveToVault = async () => {
    if (savingToVault || !vazlatRaw) return;
    const activeAlap = getActiveAlap();
    setSavingToVault(true);
    try {
      const res = await api.post("/conversion/webinar/vazlat/save", {
        voice_id: activeAlap.voice || null,
        client_id: activeAlap.client || null,
        offer_id: activeAlap.offer || null,
        webinar_cim: (vazlat?.webinar_cim || inputs?.webinar_cim || "").toString(),
        mit_tanuljon: (inputs?.mit_tanuljon || "").toString(),
        miert_te: (inputs?.miert_te || "").toString(),
        hova_hivjuk: (inputs?.hova_hivjuk || "").toString(),
        vazlat_raw: vazlatRaw,
        vazlat: vazlat || null,
      });
      const name = res?.data?.name || "Webinár vázlat";
      setSavedToVault(true);
      toast.success(`Mentve a Tudástárba: ${name}`);
      setTimeout(() => setSavedToVault(false), 2400);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Mentés sikertelen.";
      toast.error(msg);
    } finally {
      setSavingToVault(false);
    }
  };

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", color: "#e8e8f0", overflow: "hidden" }}
      data-testid="webinar-vazlat-screen"
    >
      <PostHeader
        onBackToOpening={onBack}
        activeTab="tab1"
        onTabChange={() => { /* writer után aktiválódik */ }}
        tab2Disabled={true}
        headerTitle={isDirect ? "Közvetlen értékesítés" : "Webinárból hívás"}
        tab2Label="Webinár"
      />
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">

        {/* ── BAL: Finomító panel (F3 / FB poszt minta IDENTIKUS) ── */}
        <aside
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="webinar-vazlat-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div className="font-heading" style={{
                fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                letterSpacing: "-0.015em", color: "#ffffff", marginBottom: 18,
              }}>
                Elkészült a webinár vázlata.
              </div>
              <div style={{
                fontSize: 17, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0",
              }}>
                Nézd át jobbra. Ha rendben van, indíthatod a teljes webinár generálását. Ha finomítanál rajta, jelölj ki egy részt a vázlatból, vagy írd le lent a chat-ben.
              </div>
            </div>

            {/* Selection workspace — F3 pattern */}
            <AnimatePresence>
              {(selection || isLoading || showSuggestion) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="webinar-vazlat-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.original)) && (
                    <div
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        borderLeft: "2px solid rgba(124,108,242,0.40)",
                        borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                      }}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{
                            fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                            color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                          }}>
                            KIJELÖLT RÉSZ
                          </div>
                          <div style={{
                            fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                            display: "-webkit-box", WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>
                            {(() => {
                              const q = selection || suggestion?.original || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!showSuggestion && !isLoading && (
                          <button
                            type="button"
                            onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            data-testid="webinar-vazlat-selection-clear"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <XIcon size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick chips */}
                  {!isLoading && !showSuggestion && selection && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="webinar-vazlat-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id}
                          type="button"
                          onClick={() => onQuickAction(qa)}
                          data-testid={`webinar-vazlat-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap", textAlign: "center", width: "100%",
                          }}
                        >{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {/* Loading */}
                  {isLoading && (
                    <div className="flex items-center gap-2.5" style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }} data-testid="webinar-vazlat-refine-loading">
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {/* Suggestion */}
                  {showSuggestion && (
                    <div data-testid="webinar-vazlat-suggestion">
                      <SuggestionCard
                        snippet={suggestion?.snippet || ""}
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={onApplySuggestion}
                        onRetryVariant={onRetryVariant}
                        onRetryAfterError={onRetryAfterError}
                        onCancel={onCancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Applied block */}
            <AnimatePresence>
              {showApplied && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="webinar-vazlat-applied"
                >
                  <AppliedBlock onUndo={onUndoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom chat input (FB poszt minta) */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (chatInput.trim() && !inputDisabled) submitChat();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="webinar-vazlat-finomito-form"
          >
            <div
              style={{
                background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                border: "1px solid rgba(243,238,228,0.14)",
                borderRadius: 12,
                padding: "5px 5px 5px 14px",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                opacity: inputDisabled ? 0.5 : 1,
                pointerEvents: inputDisabled ? "none" : "auto",
              }}
            >
              <button
                type="button"
                aria-label="Csatolás"
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "#8a8a9c", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Paperclip size={14} weight="regular" />
              </button>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (chatInput.trim() && !inputDisabled) submitChat();
                  }
                }}
                placeholder="Mit változtassak a kijelölt részen?"
                data-testid="webinar-vazlat-chat-input"
                disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{
                  background: "transparent", border: "none", color: "white",
                  fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                }}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || inputDisabled}
                data-testid="webinar-vazlat-chat-send"
                aria-label="Küldés"
                style={{
                  width: 36, height: 36,
                  background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                  color: "white", borderRadius: 9, border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                  opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                  boxShadow: chatInput.trim() && !inputDisabled
                    ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                    : "none",
                }}
              >
                {isLoading ? (
                  <CircleNotch size={15} weight="bold" className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={15} weight="fill" />
                )}
              </button>
            </div>
            <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
              <span>Tipp: jelölj ki egy részt jobboldalt - a kérésed arra fog vonatkozni.</span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </aside>

        {/* ── JOBB: Krém doksi (prémium, arany akcentus) ── */}
        <main
          className="overflow-y-auto h-full"
          style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
        >
          <div
            ref={containerRef}
            onMouseUp={handleMouseUp}
            data-testid="webinar-vazlat-cream"
            data-preview-theme="cream"
            className="mr-auto cream-selectable"
            style={{
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "40px 52px 60px",
              maxWidth: 880,
              marginLeft: 0,
              position: "relative",
            }}
          >
            {/* Felső akciósor — Másolás / Letöltés / Mentés a Tudástárba.
                A Mentés a vázlat fázisban DISABLED, mert a backend save
                endpoint a teljes generált webinárt várja. */}
            <div
              className="flex justify-end items-center"
              style={{ gap: 6, marginBottom: 22, position: "relative", zIndex: 1 }}
              data-html2canvas-ignore="true"
              data-testid="webinar-vazlat-actions"
            >
              <CreamActionPill
                onClick={handleCopy}
                icon={copied ? CheckCircle : CopyIcon}
                label={copied ? "Másolva" : "Másolás"}
                testid="webinar-vazlat-copy"
                accent={GOLD}
                disabled={!vazlatRaw}
              />
              <CreamActionPill
                onClick={handleDownload}
                icon={DownloadSimple}
                label="Letöltés"
                testid="webinar-vazlat-download"
                accent={GOLD}
                disabled={!vazlatRaw}
              />
              <CreamActionPill
                onClick={handleSaveToVault}
                icon={savedToVault ? CheckCircle : DownloadSimple}
                label={savingToVault ? "Mentés..." : (savedToVault ? "Mentve" : "Mentés a Tudástárba")}
                testid="webinar-vazlat-save-to-vault"
                accent={GOLD}
                active={savedToVault}
                disabled={!vazlatRaw || savingToVault}
              />
            </div>

            {/* Nagy főcím */}
            <h1 className="font-heading" style={{
              fontSize: 34, fontWeight: 700, lineHeight: 1.2,
              color: "#0a0a12", letterSpacing: "-0.02em",
              marginBottom: 14,
            }}>
              Webinár stratégiai vázlat
            </h1>

            {/* Bevezető */}
            <div style={{
              fontSize: 16, lineHeight: 1.65, color: "#3a4654",
              marginBottom: 12,
            }}>
              Az alábbi 5 blokk a teljes webinár stratégiai váza. Olvasd át, finomíts rajta a bal oldali panelen, majd indítsd el a teljes generálást.
            </div>

            <div style={{ height: 22 }} />

            {/* 01 — Webinár irány */}
            <SectionShell testid="webinar-vazlat-card-01">
              <SectionPill testid="webinar-vazlat-pill-01">01 · Webinár irány</SectionPill>
              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                <div>
                  <FieldLabel>Webinár címe</FieldLabel>
                  <PlainBlock text={vazlat?.webinar_cim || vazlat?.sections?.["1"] || ""} />
                </div>
                <div>
                  <FieldLabel>Nagy ígéret</FieldLabel>
                  <PlainBlock text={vazlat?.nagy_igeret || ""} />
                </div>
                <div>
                  <FieldLabel>Nagy ötlet</FieldLabel>
                  <PlainBlock text={vazlat?.nagy_otlet || ""} />
                </div>
              </div>
            </SectionShell>

            {/* 02 — Identitásváltás */}
            <SectionShell testid="webinar-vazlat-card-02">
              <SectionPill testid="webinar-vazlat-pill-02">02 · Identitásváltás</SectionPill>
              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                <div>
                  <FieldLabel>Régi működés</FieldLabel>
                  <PlainBlock text={vazlat?.regi_identitas || ""} />
                </div>
                <div>
                  <FieldLabel>Új működés</FieldLabel>
                  <PlainBlock text={vazlat?.uj_identitas || ""} />
                </div>
                {!vazlat?.regi_identitas && !vazlat?.uj_identitas ? (
                  <PlainBlock text={vazlat?.sections?.["4"] || ""} />
                ) : null}
              </div>
            </SectionShell>

            {/* 03 — 3 webinár-győzelem */}
            <SectionShell testid="webinar-vazlat-card-03">
              <SectionPill testid="webinar-vazlat-pill-03">03 · A 3 webinár-győzelem</SectionPill>
              {gyozelmek.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {gyozelmek.map((g, i) => (
                    <div
                      key={i}
                      data-testid={`webinar-vazlat-card-03-gyo-${i + 1}`}
                      style={{
                        padding: "18px 20px",
                        borderRadius: 10,
                        background: "#fbf4dc",
                        border: "1px solid rgba(212,168,0,0.30)",
                        boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset",
                      }}
                    >
                      <div style={{
                        fontSize: 11, color: GOLD_DEEP, letterSpacing: "0.22em",
                        textTransform: "uppercase", fontWeight: 700, marginBottom: 6,
                      }}>
                        Győzelem {i + 1}
                      </div>
                      <div className="font-heading" style={{
                        fontSize: 19, fontWeight: 700, color: "#0a0a12",
                        letterSpacing: "-0.012em", lineHeight: 1.3, marginBottom: 12,
                      }}>
                        {g.cim || ""}
                      </div>
                      {g.mit_ert_meg ? (
                        <div style={{ fontSize: 15, color: "#0a0a12", marginBottom: 7, lineHeight: 1.65 }}>
                          <span style={{ fontWeight: 700, color: "#3a4654" }}>Mit ért meg: </span>{g.mit_ert_meg}
                        </div>
                      ) : null}
                      {g.miert_fontos ? (
                        <div style={{ fontSize: 15, color: "#0a0a12", lineHeight: 1.65 }}>
                          <span style={{ fontWeight: 700, color: "#3a4654" }}>Miért fontos: </span>{g.miert_fontos}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <PlainBlock text={vazlat?.sections?.["5"] || ""} />
              )}
            </SectionShell>

            {/* 04 — Telefonhívás / Ajánlati döntés íve (variant delta) */}
            <SectionShell testid="webinar-vazlat-card-04">
              <SectionPill testid="webinar-vazlat-pill-04">{blk04Title}</SectionPill>
              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                {vazlat?.hivas_neve ? (
                  <div>
                    <FieldLabel>{blk04Field1Label}</FieldLabel>
                    <PlainBlock text={vazlat.hivas_neve} />
                  </div>
                ) : null}
                {vazlat?.hivas_fo_igeret ? (
                  <div>
                    <FieldLabel>{blk04Field2Label}</FieldLabel>
                    <PlainBlock text={vazlat.hivas_fo_igeret} />
                  </div>
                ) : null}
                {Array.isArray(vazlat?.hivas_4_dolog) && vazlat.hivas_4_dolog.length ? (
                  <div>
                    <FieldLabel>{blk04Field3Label}</FieldLabel>
                    <ul style={{
                      listStyle: "none", padding: 0, margin: 0,
                      display: "flex", flexDirection: "column", gap: 6,
                    }}>
                      {vazlat.hivas_4_dolog.slice(0, isDirect ? 6 : 4).map((b, i) => (
                        <li key={i} style={{
                          fontSize: 15, color: "#0a0a12", lineHeight: 1.7,
                          paddingLeft: 20, position: "relative",
                        }}>
                          <span style={{
                            position: "absolute", left: 0, top: 10,
                            width: 5, height: 5, borderRadius: "50%",
                            background: GOLD_DEEP,
                          }} />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  !vazlat?.hivas_neve && !vazlat?.hivas_fo_igeret ? (
                    <PlainBlock text={vazlat?.sections?.["7"] || ""} />
                  ) : null
                )}
              </div>
            </SectionShell>

            {/* 05 — Szekcióterv */}
            <SectionShell testid="webinar-vazlat-card-05">
              <SectionPill testid="webinar-vazlat-pill-05">05 · Szekcióterv</SectionPill>
              {Array.isArray(vazlat?.szekciok) && vazlat.szekciok.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {vazlat.szekciok.map((s, i) => {
                    const cim = s.cim || (typeof s === "string" ? s : "");
                    const slides = s.slide_szam || s.slide_count || s.slides || "";
                    return (
                      <div
                        key={i}
                        data-testid={`webinar-vazlat-szekcio-${i + 1}`}
                        style={{
                          display: "flex", alignItems: "baseline", gap: 14,
                          padding: "14px 0",
                          borderBottom: i < vazlat.szekciok.length - 1 ? "1px dashed rgba(10,10,18,0.10)" : "none",
                        }}
                      >
                        <div style={{
                          fontSize: 11, color: GOLD_DEEP, fontWeight: 700,
                          letterSpacing: "0.18em", minWidth: 28,
                        }}>{String(i + 1).padStart(2, "0")}</div>
                        <div style={{ flex: 1, fontSize: 15.5, color: "#0a0a12", lineHeight: 1.55 }}>{cim}</div>
                        {slides ? (
                          <div style={{ fontSize: 12.5, color: "#5b6975", whiteSpace: "nowrap", letterSpacing: "0.02em" }}>
                            {String(slides).includes("slide") ? slides : `${slides} slide`}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <PlainBlock text={vazlat?.sections?.["8"] || ""} />
              )}
            </SectionShell>

            {/* CTA — lila rendszer-gomb (MagicGenerateButton),
                IDENTIKUS a többi ügynök gombjával. */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginTop: 44 }}>
              <MagicGenerateButton
                state={(writerStarting || writerLoading) ? "generating" : "selected"}
                label={
                  writerLoading
                    ? (writerLoadingLabel || "Első szekció készül…")
                    : (writerStarting ? "Indul a writer…" : "Teljes webinár generálása")
                }
                onClick={handleStartWriter}
                disabled={writerStarting || writerLoading}
                testid="webinar-vazlat-start-writer-btn"
              />
              <button
                type="button"
                onClick={onBack}
                data-testid="webinar-vazlat-back-btn"
                style={{
                  fontSize: 12.5, color: "#5b6975", background: "transparent",
                  border: "none", cursor: "pointer", fontFamily: "inherit",
                  padding: "6px 10px",
                }}
              >← Vissza a beállításhoz</button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
